import React, { useMemo, useState } from 'react';
import useMediaQuery from "@mui/system/useMediaQuery";
import { CircularProgress, Typography, useTheme } from "@mui/joy";
import Dialog from "@mui/material/Dialog";
import { BasicModalContent } from "@dreebit-registry/vsm-microservice-components";
import { resolveErrorMessage } from "../../utils/resolveErrorMessage";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import { Box } from "@mui/system";
import _ from "lodash";
import { InstalledBaseEventTDAs } from "../../graphql/queries/installedBaseEventTDAs";
import { UpdateInstalledBaseEvent } from "../../graphql/mutations/updateInstalledBaseEvent";
import InstalledBaseTDAForm from "../InstalledBaseTDAForm";
import { separateInstalledBaseEventAttributes } from "../../utils/separateInstalledBaseEventAttributes";
import {
    decorateMaintenanceOtherAttributes,
    getMaintenanceOtherSeedValues,
    getMaintenanceOtherTriggerValues,
    nullifyInactiveMaintenanceOtherValues,
} from "../../utils/maintenanceOtherAttributes";
import { useEventTypeLabels } from "../../utils/eventTypeLabels";
import { getEventTypeIcon } from "../../utils/eventTypeIcons";
import formatEventEnum from "../../utils/formatEventEnum";
import { ManualEventType } from "../../codegen/graphql";

const DEFAULT_REFETCH = ["InstalledBaseEventList", "InstalledBaseEventById"];

interface Props {
    eventId: string;
    open: boolean;
    onClose: () => void;
    refetchQueries?: string[];
}

const UpdateInstalledBaseEventModal = ({ eventId, open, onClose, refetchQueries }: Props) => {
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const { t } = useTranslation();
    const eventTypeLabels = useEventTypeLabels();

    const { data, loading } = useQuery(InstalledBaseEventTDAs, {
        variables: { eventId },
        fetchPolicy: "network-only",
    });

    const [updateInstalledBaseEventMutation] = useMutation(UpdateInstalledBaseEvent);

    const eventType = _.get(data, 'installedBaseEvent.eventType') as ManualEventType | undefined;
    const attributes = _.get(
        data,
        'installedBaseEvent.typeDefinitionAttributeList.typeDefinitionAttributes',
        [],
    ) as any[];

    // ASSET_MAINTENANCE: each *Other free-text attribute is only shown (and required)
    // while its paired select is set to OTHER. Triggers start from the stored values
    // and follow the live form values (see maintenanceOtherAttributes.ts).
    const isMaintenance = eventType === ManualEventType.ASSET_MAINTENANCE;
    const storedTriggerValues = useMemo(
        () => getMaintenanceOtherTriggerValues(
            Object.fromEntries(attributes.map((attribute) => [attribute.index, attribute.value]))
        ),
        [attributes]
    );
    const [liveTriggerValues, setLiveTriggerValues] = useState<Record<string, unknown>>();
    const triggerValues = liveTriggerValues ?? storedTriggerValues;

    const decoratedAttributes = useMemo(
        () => (isMaintenance ? decorateMaintenanceOtherAttributes(attributes, triggerValues, t) : attributes),
        [attributes, isMaintenance, triggerValues, t]
    );

    // Keeps the *Other keys in Formik's initial values while their field is filtered
    // out (a changed key set would reset the form), preserving stored text.
    const maintenanceSeedValues = useMemo(
        () => (isMaintenance ? getMaintenanceOtherSeedValues(attributes) : undefined),
        [isMaintenance, attributes]
    );

    const handleValuesChange = (values: { [key: string]: any }) => {
        const triggers = getMaintenanceOtherTriggerValues(values);
        if (Object.keys(triggers).length === 0) return;
        setLiveTriggerValues((prev) => {
            const next = { ...(prev ?? {}), ...triggers };
            return _.isEqual(prev, next) ? prev : next;
        });
    };

    const handleSubmit = async (formValues: { [key: string]: any }) => {
        // Null the *Other texts whose trigger isn't OTHER (anymore) so stale stored
        // values are cleared on the backend rather than silently kept.
        const cleanedValues = isMaintenance ? nullifyInactiveMaintenanceOtherValues(formValues) : formValues;
        const { attributes: eventAttributes } = separateInstalledBaseEventAttributes(cleanedValues, attributes);
        try {
            await updateInstalledBaseEventMutation({
                variables: {
                    eventId,
                    input: { attributes: eventAttributes },
                },
                refetchQueries: refetchQueries ?? DEFAULT_REFETCH,
            });
            toast.success(t("Successfully updated event"));
            onClose();
        } catch (err) {
            toast.error(resolveErrorMessage(err, t));
        }
    };

    const titleText = eventType
        ? (eventTypeLabels[eventType] ?? formatEventEnum(eventType, t))
        : t('Edit event');

    const renderBody = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (attributes.length === 0) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px', p: 2 }}>
                    <Typography level="body-md" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                        {t("No editable attributes available for this event.")}
                    </Typography>
                </Box>
            );
        }

        return (
            <InstalledBaseTDAForm
                attributes={decoratedAttributes}
                onSubmit={handleSubmit}
                onValuesChange={isMaintenance ? handleValuesChange : undefined}
                initialFormValues={maintenanceSeedValues}
                submitButtonText={t('Save')}
            />
        );
    };

    return (
        <Dialog open={open} onClose={onClose} fullScreen={isFullScreen}>
            <BasicModalContent
                startIcon={eventType ? (getEventTypeIcon(eventType) ?? undefined) : undefined}
                onClose={onClose}
                title={<Typography level={'titleSmallLight'}>{titleText}</Typography>}
            >
                <Box sx={{ position: 'relative' }}>
                    {renderBody()}
                </Box>
            </BasicModalContent>
        </Dialog>
    );
};

export default UpdateInstalledBaseEventModal;
